import { REQUIRED_MESSAGES } from '@srm/ui/lib/requiredMessage';
import '../ActivityActions.scss';
import React, { useEffect, useState } from 'react';
import { AxiosResponse } from 'axios';
import { useForm } from 'react-hook-form';
import { IAddressBookData } from '@models/addressbook/IAddressBookData';
import { IAbookOptions } from '@models/Forms/IAbookOptions';
import { IFormValues } from '@models/Forms/IForms';
import { getABookFormValues } from '@utils/abookUtils';
import ModalContainer, { IModalContainerProps } from '@molecules/ModalContainer/ModalContainer';
import { useActionSubmit } from '@organisms/actions/useActionSubmit';
import { useUnsavedFormInput } from '@srm/ui/hooks/useUnsavedFormInput';
import AbookpickerControl from '@controls/AbookpickerControl';
import TextareaControl from '@srm/ui/controls/TextareaControl';
import { requiredTextRules } from '@srm/ui/lib/requiredTextRules';
import { ModalSize } from '@srm/ui/types/ModalSize';
import { useDocumentAbookTabs } from '@/hooks/useDocumentAbookTabs';

export interface IExecutorPickerFormData {
    executor: string;
    comment?: string;
}

/** Общий набор пропсов действий «сменить исполнителя» */
export interface IExecutorActionProps {
    activityId: string;
    docId: string;
    actionKey: string;
    title: string;
    okButtonText: string;
    cancelButtonText: string;
    modalSize: ModalSize;
    completeHandler?: (isSucceed: boolean) => void;
}

interface IExecutorPickerModalProps extends IExecutorActionProps {
    /** Модель действия: настройки адресной книги для пикера */
    loadModel: () => Promise<AxiosResponse<IAbookOptions>>;
    submit: (form: IExecutorPickerFormData) => Promise<unknown>;
    /** Обязательный комментарий под пикером */
    showComment?: boolean;
    /** Своё сообщение вместо тихого отказа, если исполнитель не выбран */
    guardExecutor?: boolean;
}

/**
 * «Пикер исполнителя + комментарий»: модель действия грузится при открытии,
 * дальше форма из одного-двух полей и сабмит. На этом живут «Делегировать»
 * и «Заменить исполнителя».
 */
export const ExecutorPickerModal: React.FC<IExecutorPickerModalProps> = ({
    loadModel,
    submit,
    showComment,
    guardExecutor,
    ...props
}: IExecutorPickerModalProps) => {
    const [executor, setExecutor] = useState<IAddressBookData>();
    const [abookOptions, setAbookOptions] = useState<IAbookOptions>();
    const abookTabs = useDocumentAbookTabs(props.docId, abookOptions?.tabs);
    const [formValues, setFormValues] = useState<string>('{}');
    const activated = React.useRef(false);

    const { errorText, setErrorText, isBusy, setIsBusy, isSubmitting, okClick, cancelClick } = useActionSubmit({
        completeHandler: props.completeHandler,
        initialBusy: true,
        submit,
    });

    const getFormValues = async (fValues?: IFormValues) => {
        const vals = await getABookFormValues(props.docId, fValues);
        if (activated.current && vals) {
            setFormValues(vals);
        }
    };

    const onSubmit = (form: IExecutorPickerFormData) => {
        if (guardExecutor && !form?.executor) {
            setErrorText('Не указан исполнитель');
            return;
        }

        return okClick(form);
    };

    const formState = useForm<IExecutorPickerFormData>({
        mode: 'onBlur', // "onChange"
    });
    const hasUnsavedInput = useUnsavedFormInput(formState, { textFields: ['comment'] });

    useEffect(() => {
        // Закрытое окно ответ подготовки не применяет.
        let ignore = false;
        setIsBusy(true);
        activated.current = true;

        loadModel()
            .then((res) => {
                if (ignore) return;
                setAbookOptions(res.data);
                void getFormValues(res.data?.formValues);
            })
            .catch(() => {
                if (!ignore) setErrorText('Ошибка получения данных от сервера.');
            })
            .finally(() => {
                if (!ignore) setIsBusy(false);
            });
        return () => {
            ignore = true;
            activated.current = false;
        };
    }, []);

    const modalContainerProps: IModalContainerProps = {
        header: props.title,
        size: props.modalSize,
        showOkButton: true,
        showCancelButton: true,
        okButtonText: props.okButtonText,
        cancelButtonText: props.cancelButtonText,
        loading: isBusy,
        closeDisabled: isSubmitting,
        hasUnsavedInput,
        onCancel: cancelClick,
        errorText: errorText,
        formMethods: formState,
        onSubmit: onSubmit,
        okButtonDisabled: false,
    };

    return (
        <ModalContainer {...modalContainerProps}>
            <AbookpickerControl
                label="Исполнитель"
                placeholder="Исполнитель"
                isMultiple={false}
                title="Исполнитель"
                displayValue={executor?.fullName ?? ''}
                formValues={formValues}
                name="executor"
                tabsSettings={abookTabs}
                externalSearch={abookOptions?.externalSearch}
                required
                rules={{ required: REQUIRED_MESSAGES.select }}
                control={formState.control}
                showChips={false}
                onSetValues={(value: IAddressBookData[]) => {
                    setExecutor(value[0]);
                }}
            />

            {showComment && (
                <TextareaControl
                    label="Комментарий"
                    rows={6}
                    readOnly={false}
                    required={true}
                    rules={requiredTextRules()}
                    name="comment"
                    control={formState.control}
                />
            )}
        </ModalContainer>
    );
};
